import { useEffect, type RefObject } from 'react'

/**
 * Uvězní fokus uvnitř modalu: Tab/Shift+Tab cyklí jen po prvcích v containeru,
 * Escape zavře, a po zavření se fokus vrátí na prvek, ze kterého se modal otevřel.
 * Přístupnost: WCAG 2.1.2 (No Keyboard Trap — správně řízený) a 2.4.3 (Focus Order).
 *
 * Použití: const ref = useRef<HTMLDivElement>(null); useFocusTrap(ref, onClose)
 * — `ref` dej na kořenový element modalu, volej jen když je modal otevřený (mountnutý).
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, onClose?: () => void, active = true) {
  useEffect(() => {
    if (!active) return
    const el = ref.current
    if (!el) return
    const prevFocused = document.activeElement as HTMLElement | null
    const selector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    const items = () => Array.from(el.querySelectorAll<HTMLElement>(selector)).filter((x) => x.offsetParent !== null)

    // Počáteční fokus na první prvek v modalu.
    items()[0]?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose?.(); return }
      if (e.key !== 'Tab') return
      const list = items()
      if (list.length === 0) return
      const first = list[0]
      const last = list[list.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus() }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      // Vrať fokus tam, kde byl (např. na tlačítko „Sdílet").
      prevFocused?.focus?.()
    }
  }, [ref, onClose, active])
}
